import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  DataTable,
  Dialog,
  Field,
  Input,
  PageHead,
  Panel,
  SelectX,
  StatusPill,
  Toggle,
  useToast,
  type Column,
} from "@guestpilot/ui-kit";
import { fieldErrorsOf, useGet, useHandleApiError, useWrite, type Schema } from "../api";
import { ConfirmDialog } from "../components";
import { formatDateTime } from "../format";

type Flag = Schema<"FeatureFlag">;
type Write = Schema<"FeatureFlagWrite">;
type Override = Schema<"FlagOverride">;

const SCOPES = [
  { value: "all", label: "All customers" },
  { value: "trial_only", label: "Trial customers only" },
  { value: "beta_opt_in", label: "Beta (opt-in)" },
];

/** Flags ship features independently of plans (BR-249): a flag has a default and a rollout scope, never a plan gate. */
export function FeatureFlags() {
  const list = useGet<Flag[]>("/feature-flags");
  const fail = useHandleApiError();
  const toast = useToast();
  const update = useWrite<Flag, Write>("PUT", "/feature-flags/:key", ["/feature-flags"]);
  const [edit, setEdit] = useState<Flag | "new" | null>(null);
  const [overrides, setOverrides] = useState<Flag | null>(null);
  const [change, setChange] = useState<{ f: Flag; on: boolean } | null>(null);
  const columns: Column<Flag>[] = [
    {
      key: "k",
      header: "Flag",
      render: (f) => (
        <div className="gp-person-text">
          <span className="t-body-strong t-mono">{f.key}</span>
          <span className="gp-person-sub t-body-sm">{f.description}</span>
        </div>
      ),
    },
    {
      key: "d",
      header: "Default",
      render: (f) => (
        <Toggle
          label={`${f.key} on by default`}
          checked={f.defaultEnabled ?? false}
          onChange={(v) => setChange({ f, on: v })}
        />
      ),
    },
    {
      key: "s",
      header: "Rollout",
      render: (f) => (
        <StatusPill
          domain="flagScope"
          value={f.rolloutScope ?? "all"}
          label={SCOPES.find((s) => s.value === f.rolloutScope)?.label ?? ""}
        />
      ),
    },
    {
      key: "o",
      header: "Overrides",
      render: (f) =>
        (f.overrideCount ?? 0) > 0 ? (
          <Button variant="ghost" onClick={() => setOverrides(f)}>
            {f.overrideCount} customers
          </Button>
        ) : (
          <span className="gp-muted">None</span>
        ),
    },
    {
      key: "e",
      header: "Actions",
      render: (f) => (
        <Button variant="ghost" onClick={() => setEdit(f)}>
          Edit
        </Button>
      ),
    },
  ];
  return (
    <div className="gp-page">
      <PageHead
        title="Feature flags"
        subtitle="Roll features out independently of plans."
        actions={
          <Button variant="primary" onClick={() => setEdit("new")}>
            New flag
          </Button>
        }
      />
      <Panel label="Feature flags">
        <DataTable
          caption="Feature flags"
          columns={columns}
          rows={list.data}
          loading={list.isLoading}
          error={list.isError}
          onRetry={() => void list.refetch()}
          errorTitle="Could not load the flags."
          retryLabel="Retry"
          rowKey={(f) => f.key ?? ""}
          emptyTitle="No feature flags yet"
        />
      </Panel>
      <ConfirmDialog
        open={change !== null}
        title={`Turn ${change?.f.key} ${change?.on ? "on" : "off"} by default?`}
        description="This changes the default for every customer in the rollout scope that has no override."
        confirmLabel={change?.on ? "Turn on" : "Turn off"}
        busy={update.isPending}
        onClose={() => setChange(null)}
        onConfirm={() =>
          change &&
          update.mutate(
            {
              params: { key: change.f.key ?? "" },
              body: {
                key: change.f.key ?? "",
                description: change.f.description ?? "",
                defaultEnabled: change.on,
                rolloutScope: change.f.rolloutScope ?? "all",
              },
            },
            {
              onSuccess: () => {
                toast.success("Default changed");
                setChange(null);
              },
              onError: fail,
            },
          )
        }
      />
      {edit ? (
        <FlagForm
          key={edit === "new" ? "new" : edit.key}
          flag={edit === "new" ? null : edit}
          onClose={() => setEdit(null)}
          toast={toast.success}
        />
      ) : null}
      {overrides ? <Overrides flag={overrides} onClose={() => setOverrides(null)} /> : null}
    </div>
  );
}

function FlagForm({
  flag,
  onClose,
  toast,
}: {
  flag: Flag | null;
  onClose: () => void;
  toast: (m: string) => void;
}) {
  const fail = useHandleApiError();
  const create = useWrite<Flag, Write>("POST", "/feature-flags", ["/feature-flags"]);
  const update = useWrite<Flag, Write>("PUT", "/feature-flags/:key", ["/feature-flags"]);
  const [key, setKey] = useState(flag?.key ?? "");
  const [description, setDescription] = useState(flag?.description ?? "");
  const [on, setOn] = useState(flag?.defaultEnabled ?? false);
  const [scope, setScope] = useState<Write["rolloutScope"]>(flag?.rolloutScope ?? "all");
  const [errs, setErrs] = useState<Record<string, string>>({});
  const badKey = !/^[a-z][a-z0-9_]{2,63}$/.test(key);
  const m = flag ? update : create;
  return (
    <ConfirmDialog
      open
      title={flag ? `Edit ${flag.key}` : "New flag"}
      confirmLabel={flag ? "Save" : "Create flag"}
      busy={m.isPending}
      disabledReason={
        badKey
          ? "Use lower-case letters, digits and underscores (3 to 64, starting with a letter)."
          : !description.trim()
            ? "Describe the flag."
            : undefined
      }
      onClose={onClose}
      onConfirm={() =>
        m.mutate(
          {
            params: { key },
            body: { key, description: description.trim(), defaultEnabled: on, rolloutScope: scope },
          },
          {
            onSuccess: () => {
              toast(flag ? "Flag saved" : "Flag created");
              onClose();
            },
            onError: (e) => {
              setErrs(fieldErrorsOf(e));
              fail(e);
            },
          },
        )
      }
    >
      <Field label="Key" help="Cannot be changed later." error={errs.key}>
        <Input
          className="t-mono"
          value={key}
          disabled={flag !== null}
          onChange={(e) => setKey(e.target.value)}
        />
      </Field>
      <Field label="Description" error={errs.description}>
        <Input value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Rollout">
        <SelectX
          value={scope}
          options={SCOPES}
          onChange={(e) => setScope(e.target.value as Write["rolloutScope"])}
        />
      </Field>
      <Toggle label="On by default" checked={on} onChange={setOn} />
    </ConfirmDialog>
  );
}

function Overrides({ flag, onClose }: { flag: Flag; onClose: () => void }) {
  const list = useGet<Override[]>(`/feature-flags/${flag.key}/overrides`);
  const columns: Column<Override>[] = [
    {
      key: "c",
      header: "Customer",
      render: (o) => <Link to={`/customers/${o.organizationId}`}>{o.organizationId}</Link>,
    },
    { key: "e", header: "State", render: (o) => (o.enabled ? "On" : "Off") },
    { key: "r", header: "Reason", render: (o) => o.reason ?? "—" },
    { key: "d", header: "Since", render: (o) => formatDateTime(o.createdAt) },
  ];
  return (
    <Dialog
      open
      title={`Overrides of ${flag.key}`}
      size="lg"
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <DataTable
        caption={`Overrides of ${flag.key}`}
        columns={columns}
        rows={list.data}
        loading={list.isLoading}
        error={list.isError}
        onRetry={() => void list.refetch()}
        errorTitle="Could not load the overrides."
        retryLabel="Retry"
        rowKey={(o) => o.id ?? ""}
        emptyTitle="No overrides"
      />
    </Dialog>
  );
}
